import { DateRange, Declaration, DeclarationFilterField, DeclarationFilters } from '../types/declaration';
import { PdfRegistryCard, PdfRegistryField, PdfSummaryData, PdfTotalCard } from '../types/registryPdf';
import { formatDate } from './dateUtils';
import {
    getApplicantName,
    getDeclarationStatus,
    getLabNames,
    getRegYear,
    getStatusColor,
    getStatusCounts,
} from './declarationUtils';
import { buildTotalCard, formatFilterDescription, getStatusBadgeTone, isMissing } from './registryPdfUtils';

const FIELD_FILTER_LABELS: { [field in DeclarationFilterField]: string } = {
    reg_number: 'номер декларации',
    product_kind: 'вид продукции',
    applicant_name: 'заявитель',
    applicant_inn: 'ИНН заявителя',
    manufacturer_full_name: 'изготовитель',
    manufacturer_inn: 'ИНН изготовителя',
    lab_names: 'лаборатория',
};


const formatDateRange = (range: DateRange): string[] => {
    if (!range.start && !range.end) return [];
    const parts: string[] = [];
    if (range.start) parts.push(`с ${formatDate(range.start)}`);
    if (range.end) parts.push(`по ${formatDate(range.end)}`);
    return [parts.join(' ')];
};


export const describeDeclarationFilters = (filters: DeclarationFilters): string => {
    const conditions = [
        { label: 'статус', values: filters.statuses },
        { label: 'год', values: [...filters.years].sort(), quoted: false },
        { label: 'дата регистрации', values: formatDateRange(filters.regDate), quoted: false },
        { label: 'дата окончания', values: formatDateRange(filters.endDate), quoted: false },
    ];
    (Object.keys(filters.fields) as DeclarationFilterField[]).forEach(field => {
        conditions.push({ label: FIELD_FILTER_LABELS[field], values: filters.fields[field] });
    });
    return formatFilterDescription(conditions);
};


// Как на странице реестра, заявители и изготовители по ИНН, без ИНН по названию
const countUnique = (values: (string | null)[]): number => {
    return new Set(values.filter(Boolean)).size;
};


// Статусы и цвета как на диаграмме статусов на странице реестра, с динамикой по годам
export const buildDeclarationStatusCard = (title: string, declarations: Declaration[]): PdfTotalCard => {
    const statusCategories = getStatusCounts(declarations).map((statusCount, index) => ({
        label: statusCount.status,
        color: getStatusColor(statusCount.status, index),
        matches: (declaration: Declaration) => getDeclarationStatus(declaration) === statusCount.status,
    }));
    return buildTotalCard(title, declarations, statusCategories, getRegYear, true);
};


export const buildDeclarationsSummary = (declarations: Declaration[], basisLabel: string, filterDescription: string): PdfSummaryData => {

    const labNames: string[] = [];
    declarations.forEach(declaration => labNames.push(...getLabNames(declaration)));

    return {
        title: `Отчёт по реестру деклараций ${basisLabel}`,
        subtitle: `Фильтр: ${filterDescription}`,
        counters: [
            { label: 'Общее количество деклараций', value: declarations.length },
            { label: 'Общее количество заявителей', value: countUnique(declarations.map(declaration => declaration.applicant_inn || declaration.applicant_full_name)) },
            { label: 'Общее количество изготовителей', value: countUnique(declarations.map(declaration => declaration.manufacturer_inn || declaration.manufacturer_full_name)) },
            { label: 'Общее количество лабораторий', value: countUnique(labNames) },
        ],
        cards: [
            buildDeclarationStatusCard('Статус декларации в реестре деклараций о соответствии ФСА', declarations),
        ],
    };
};


export const buildDeclarationRegistryCard = (declaration: Declaration): PdfRegistryCard => {
    const status = getDeclarationStatus(declaration);

    // Названия и номера лабораторий записаны в двух строках через ; в одном порядке
    const labNumbers = (declaration.lab_reg_numbers || '').split(';').map(labNumber => labNumber.trim());
    const labs = (declaration.lab_names || '').split(';')
        .map((labName, index) => ({ value: labName.trim(), hint: labNumbers[index] || '' }))
        .filter(lab => !isMissing(lab.value));

    const manufacturerHints: string[] = [];
    if (declaration.manufacturer_inn) manufacturerHints.push(`ИНН ${declaration.manufacturer_inn}`);
    if (declaration.product_origin) manufacturerHints.push(declaration.product_origin);

    const fields: PdfRegistryField[] = [
        {
            label: 'Срок действия',
            entries: [{ value: [formatDate(declaration.reg_date || ''), formatDate(declaration.end_date || '')].filter(Boolean).join(' – ') }],
        },
        { label: 'Схема декларирования', entries: [{ value: declaration.decl_schema || '' }] },
        { label: 'Вид продукции', entries: [{ value: declaration.product_kind || '' }] },
        {
            label: 'Заявитель',
            entries: [{ value: getApplicantName(declaration), hint: declaration.applicant_inn ? `ИНН ${declaration.applicant_inn}` : '' }],
        },
        {
            label: 'Изготовитель',
            entries: [{ value: declaration.manufacturer_full_name || '', hint: manufacturerHints.join(' · ') }],
        },
        { label: labs.length > 1 ? 'Лаборатории' : 'Лаборатория', entries: labs },
    ];
    if (declaration.termination_date) {
        fields.splice(1, 0, {
            label: 'Прекращение',
            entries: [{ value: formatDate(declaration.termination_date), hint: declaration.termination_reason || '' }],
        });
    }

    return {
        number: declaration.reg_number,
        status: { text: status, tone: getStatusBadgeTone(status) },
        fields,
        sections: [
            { title: 'Продукция', text: declaration.product_full_name || '' },
            { title: 'Изготовлено в соответствии', text: declaration.tech_documents || '' },
            { title: 'Стандарты', text: declaration.standards || '' },
        ],
    };
};
